<template>
  <div
    v-if="showThemeBuilder"
    class="ThemeBuilder"
  >
    <span class="Icon arrow-left" />

    <div v-if="ui.showThemeEdit">
      <div class="ThemeHeader">
        <div>{{ ui.themeHeader }}</div>
      </div>

      <div class="ThemeContent">
        <theme-editor />
      </div>

      <div class="ThemeFooter">
        <button
          v-if="!ui.shouldHideRemoveButton"
          class="Button Button--danger-outline"
          @click="cancelEdit"
        >
          Cancel
        </button>
        <button
          class="Button Button--green u-floatRight"
          @click="submitTheme"
        >
          Save
        </button>
      </div>
    </div>

    <div v-else>
      <div class="ThemeHeader">
        <div>Themes</div>
        <div @click.stop="showCreateThemeForm">
          <button class="Button Button-add-theme">
            <span class="Icon Icon-plus" />
          </button>
        </div>
      </div>

      <div class="ThemeContent">
        <div>
          <div style="display: inline-flex; flex-wrap: wrap; gap: 15px">
            <theme-card
              v-for="theme in themes"
              :key="theme.id"
              :theme="theme"
              :show-edit-menu="theme.id === ui.showThemeCardId"
              style="align-self:flex-end"
              @toggle-card-menu="toggleCardEditMenu(theme.id)"
              @preview-theme="previewSelectedTheme(theme)"
              @edit-theme="showEditThemeForm(theme.id)"
              @duplicate-theme="duplicateThemeById(theme.id)"
              @delete-theme="deleteTheme(theme.id)"
            />
          </div>
        </div>
      </div>

      <div class="ThemeFooter">
        <button
          class="Button Button--green u-floatRight"
          @click="saveSelectedTheme"
        >
          Done
        </button>
      </div>
    </div>
  </div>
</template>
<script>

import {
  mapState,
  mapActions,
  mapMutations,
  mapGetters,
} from 'vuex';

import ThemeCard from './theme-card.vue';
import ThemeEditor from './theme-editor.vue';

export default {
  components: {
    ThemeCard,
    ThemeEditor,
  },

  props: {
    showThemeBuilder: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      ui: {
        shouldHideRemoveButton: false,
        submitLoading: false,
        showThemeEdit: false,
        showThemeCardId: null,
        themeHeader: '',
      },
    };
  },

  computed: {
    ...mapState('themes', {
      themes: (state) => state.themes,
      previewTheme: (state) => state.previewTheme,
      editableThemeId: (state) => state.editableThemeId,
    }),

    ...mapState('events', {
      event: (state) => state.event,
    }),

    ...mapGetters('themes', [
      'getSelectedTheme',
      'getEditableThemeById',
    ]),
  },

  watch: {
    showThemeBuilder: {
      handler() {
        this.ui.showThemeEdit = false;
        this.resetSelectedTheme();
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('themes', [
      'storeTheme',
      'updateTheme',
      'destroyTheme',
      'duplicateTheme',
      'updateSelectedTheme',
    ]),

    ...mapMutations('themes', [
      'setPreviewTheme',
      'resetSelectedTheme',
      'setEditableThemeId',
      'setSelectedThemeById',
    ]),

    showCreateThemeForm() {
      this.setEditableThemeId(null);
      this.ui.themeHeader = 'Create Theme';
      this.ui.showThemeEdit = true;
      this.ui.showThemeCardId = null;
    },

    showEditThemeForm(themeId) {
      this.setEditableThemeId(themeId);
      this.ui.themeHeader = 'Edit Theme';
      this.ui.showThemeEdit = true;
      this.ui.showThemeCardId = null;
    },

    toggleCardEditMenu(themeId) {
      this.ui.showThemeCardId = (this.ui.showThemeCardId !== themeId) ? themeId : null;
    },

    previewSelectedTheme(theme) {
      this.setPreviewTheme(theme);

      this.setSelectedThemeById(theme.id);

      this.ui.showThemeCardId = null;
    },

    cancelEdit() {
      this.setPreviewTheme(this.getSelectedTheme);

      this.ui.showThemeCardId = null;
      this.ui.showThemeEdit = false;
    },

    submitTheme() {
      const payload = {
        formId: this.$route.params.id,
        theme: this.previewTheme,
      };

      if (this.previewTheme.id === null) {
        this.storeTheme(payload)
          .then(() => {
            this.ui.showThemeEdit = false;
            this.$bus.$emit('displayMsg', {
              text: 'Theme saved',
              type: 'success',
            });
          })
          .catch((data) => {
            this.formatValidationMessage(data);
          });
      } else {
        this.updateTheme(payload)
          .then(() => {
            this.ui.showThemeEdit = false;
            this.$bus.$emit('displayMsg', {
              text: 'Theme updated',
              type: 'success',
            });
          })
          .catch((data) => {
            this.formatValidationMessage(data);
          });
      }
    },

    saveSelectedTheme() {
      this.ui.showThemeCardId = null;

      const payload = {
        formId: this.$route.params.id,
        themeId: this.getSelectedTheme.id,
      };

      this.updateSelectedTheme(payload)
        .then(() => {
          this.$emit('toggleThemeBuilder');
          this.$bus.$emit('displayMsg', {
            text: 'Theme saved',
            type: 'success',
          });
        })
        .catch((data) => {
          this.$bus.$emit('displayMsg', {
            text: data.data.message,
            type: 'error',
            duration: 25000,
          });
        });
    },

    duplicateThemeById(themeId) {
      this.ui.showThemeCardId = null;

      const payload = {
        formId: this.$route.params.id,
        themeId,
      };

      this.duplicateTheme(payload)
        .then(() => {
          this.ui.showThemeEdit = false;
          this.$bus.$emit('displayMsg', {
            text: 'Theme was duplicated successfully',
            type: 'success',
          });
        })
        .catch((data) => {
          this.$bus.$emit('displayMsg', {
            text: data.data.message,
            type: 'error',
            duration: 25000,
          });
        });
    },

    deleteTheme(themeId) {
      this.ui.showThemeCardId = null;

      const payload = {
        formId: this.$route.params.id,
        theme: this.getEditableThemeById(themeId),
      };

      this.destroyTheme(payload)
        .then(() => {
          this.$emit('toggleThemeBuilder');
          this.$bus.$emit('displayMsg', {
            text: 'Theme deleted',
            type: 'success',
          });
        })
        .catch((data) => {
          this.$bus.$emit('displayMsg', {
            text: data.data.message,
            type: 'error',
            duration: 25000,
          });
        });
    },

    formatValidationMessage({ data }) {
      let { message } = data.data;

      data.meta.forEach((error) => {
        message += `<rmp-break-line>${error.message}`;
      });

      this.ui.submitLoading = false;

      this.$bus.$emit('displayMsg', {
        text: message,
        type: 'error',
        duration: 25000,
      });
    },

  },
};
</script>

<style scoped lang="scss">
.ThemeBuilder {
  position: absolute;
  height: 610px;
  width: 439px;
  padding: 25px;
  z-index: 2;
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  box-shadow: 0 13px 29px 0 rgba(0, 0, 0, 0.11);
  background-color: #ffffff;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-content: space-between;

  .ThemeHeader {
    height: 41px;
    padding-bottom: 5px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    > div {
      display: flex;
      align-items: center;
      font-size: 20px;
      color: #083555;
      font-weight: 400;
    }
  }

  .ThemeContent {
    overflow: auto;
    height: 450px;
    justify-content: stretch;
    margin-bottom: 15px;
    padding-right: 3px;
  }

  .ThemeFooter {
    width: 100%;
    margin-top: auto;
    justify-content: space-between;
  }

  .Button-add-theme {
    padding: 10px;
    min-width: 0px !important;
  }

  &.dynamic {
    position: absolute;
  }

  .Selected {
    &.QuestionUuid {
      background: #046ff0;
      color: white;
      padding: 4px 15px;
      text-transform: capitalize;
      margin-bottom: -2px;
      font-size: 0.8rem;
    }

    &.Question-container {
      border: 1px solid #046ff0;
    }
  }

  .MultiChoiceImage-container {
    background: #eef1f3;
    padding: 10px;
    border-radius: 4px;
    border: 1px solid #dddddd;
    align-self: baseline;

    .Form-field {
      margin: 6px 3px 0 0;
      width: 94%;
      display: inline-block;
    }

    ::v-deep .Form-input {
      height: 28px;
    }
  }

  .Icon.arrow-left {
    display: block;
    position: absolute;
    left: -12px;
    top: 108px;
    width: 0;
    height: 0;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    border-right: 12px solid #ffffff;
    z-index: 1;
  }
}

.Fieldset-collapsible {
  .Fieldset-body.collapsed {
    display: none;
  }
}

</style>
